import gql from 'graphql-tag';
import companyFragment from './fragments/moveiq-company';
import companyDetailFragment from './fragments/moveiq-company-detail';
import currentMoveFragment from './fragments/current-move';
import inventoryOutputFragment from './fragments/inventory-output';
import inventoryDeltasFragment from './fragments/inventory-deltas';
import moveIqMoveFragment from './fragments/moveiq-move';
import moveIqEstimateRequestFragment from './fragments/moveiq-estimate-request';
import moveIqEstimateFragment from './fragments/moveiq-estimate';
import offersOutputFragment from './fragments/offers-output';
import reservationFragment from './fragments/moveiq-reservation';
import sessionOutputFragment from './fragments/session-output';
import { transformRemoteInput } from './utils';
import { pick } from 'lodash-es';
import { reduxStore } from '../common/redux';

export const remote = {
  createPublicSession: () => ({
    mutation: gql`
      mutation createPublicSession($input: CreatePublicSessionInput) {
        createPublicSession(input: $input) {
          ...${sessionOutputFragment.name}
        }
      }
      ${sessionOutputFragment.fragment}
    `,
    variables: {
      input: {
        source: 'moveiq',
      },
    },
  }),

  generateMoveIqOffers: (moveId, companyId = null, weight = null) => ({
    mutation: gql`
        mutation generateMoveIqOffers($input: GenerateMoveIqOffersInput) {
          generateMoveIqOffers(input: $input) {
            ...${offersOutputFragment.name}
          }
        }
        ${offersOutputFragment.fragment}
      `,
    variables: {
      input: { moveId, companyId, weight },
    },
  }),

  makeMoveIqReservation: input => ({
    mutation: gql`
      mutation makeMoveIqReservation($input: MakeMoveIqReservationInput) {
        makeMoveIqReservation(input: $input) {
          reservation {
            ...${reservationFragment.name}
          }
        }
      }
      ${reservationFragment.fragment}
    `,
    variables: {
      input: transformRemoteInput(input),
    },
  }),

  moveIqCompany: id => ({
    query: gql`
      query moveIqCompany($id: ID!) {
        moveIqCompany(id: $id)  {
          ...${companyFragment.name}
        }
      }
      ${companyFragment.fragment}
    `,
    variables: {
      id,
    },
  }),

  moveIqCompanyDetail: id => ({
    query: gql`
      query moveIqCompany($id: ID!) {
        moveIqCompany(id: $id)  {
          ...${companyDetailFragment.name}
        }
      }
      ${companyDetailFragment.fragment}
    `,
    variables: {
      id,
    },
  }),

  moveIqInventory: moveId => ({
    query: gql`
      query moveIqInventory($moveId: ID) {
        moveIqInventory(moveId: $moveId)  {
          ...${inventoryOutputFragment.name}
        }
      }
      ${inventoryOutputFragment.fragment}
    `,
    variables: {
      moveId,
    },
    fetchPolicy: 'network-only',
  }),

  moveIqMove: moveId => ({
    query: gql`
      query remoteMoveIqMove{
        moveIqMove(moveId: ${moveId}) {
          ...${moveIqMoveFragment.name}
        }
      }
      ${moveIqMoveFragment.fragment}
    `,
  }),

  evaluateMoveIqEligibility: () => ({
    mutation: gql`
      mutation evaluateMoveIqEligibility(
        $input: EvaluateMoveIqEligibilityInput!
      ) {
        evaluateMoveIqEligibility(input: $input) {
          eligible
          moveType
        }
      }
    `,
    variables: {
      input: {
        moveId: reduxStore().getState().moveId,
      },
    },
  }),

  moveIqReservation: moveId => ({
    query: gql`
      query moveIqReservation($moveId: ID) {
        moveIqReservation(moveId: $moveId) {
          ...${reservationFragment.name}
        }
      }
      ${reservationFragment.fragment}
    `,
    variables: {
      moveId,
    },
  }),

  moveIqSurveyAvailability: input => ({
    query: gql`
      query moveIqSurveyAvailability($input: SurveyAvailabilityInput!) {
        moveIqSurveyAvailability(input: $input) {
          ...${companyFragment.name}
        }
      }
      ${companyFragment.fragment}
    `,
    variables: {
      input: {
        ...transformRemoteInput(input),
        moveType: reduxStore().getState().moveType,
      },
    },
  }),

  setMoveIqMove: input => ({
    mutation: gql`
      mutation setMoveIqMove($input: SetMoveIqMoveInput) {
        setMoveIqMove(input: $input) {
          move {
            ...${moveIqMoveFragment.name}
          }
        }
      }
      ${moveIqMoveFragment.fragment}
    `,
    variables: {
      input: transformsSetMoveIqMoveRemoteInput(input),
    },
  }),

  updateMoveIqInventory: input => ({
    mutation: gql`
      mutation updateMoveIqInventory($input: UpdateMoveIqInventoryInput) {
        updateMoveIqInventory(input: $input)  {
          ...${inventoryOutputFragment.name}
        }
      }
      ${inventoryOutputFragment.fragment}
    `,
    variables: {
      // TODO: Not using `transformRemoteInput` here b/c it appears to destroy
      // array values (it isn't needed here currently, anyway).
      input,
    },
  }),

  moveIqEstimateRequest: moveId => ({
    query: gql`
      query moveIqEstimateRequest($moveId: ID) {
        moveIqEstimateRequest(moveId: $moveId) {
          ...${moveIqEstimateRequestFragment.name}
        }
      }
      ${moveIqEstimateRequestFragment.fragment}
    `,
    variables: {
      moveId,
    },
  }),

  makeMoveIqEstimateRequest: input => ({
    mutation: gql`
      mutation makeMoveIqEstimateRequest(
        $input: MakeMoveIqEstimateRequestInput
      ) {
        makeMoveIqEstimateRequest(input: $input) {
          estimateRequest {
            ...${moveIqEstimateRequestFragment.name}
          }
        }
      }
      ${moveIqEstimateRequestFragment.fragment}
    `,
    variables: {
      input: {
        ...makeEstimatesRequestInput(input),
        moveType: reduxStore().getState().moveType || 'local',
      },
    },
  }),

  searchMoveIqBallparkPrices: input => ({
    query: gql`
      query SearchMoveIqBallparkPrices(
        $input: SearchMoveIqBallparkPricesInput!
      ) {
        searchMoveIqBallparkPrices(input: $input) {
          ballparkPrices {
            homeSize
            minPrice
            maxPrice
          }
        }
      }
    `,
    variables: {
      input,
    },
  }),
};

export const client = {
  currentMove: () => ({
    query: gql`
      query {
        CurrentMove @client {
          ...${currentMoveFragment.name}
        }
      }
      ${currentMoveFragment.fragment}
    `,
  }),

  moveIqMove: moveId => ({
    query: gql`
      query {
        moveIqMove(moveId: ${moveId}) @client {
          ...${moveIqMoveFragment.name}
        }
      }
    ${moveIqMoveFragment.fragment}
    `,
    // This is confusing as hell because `@client` means we're reading from
    // the cache. However, unless we specify `no-cache`, and the lookup fails,
    // `apollo` will cache `null` as the value and you'll tear your hair out.
    fetchPolicy: 'no-cache',
  }),

  session: () => ({
    query: gql`
      query {
        session @client {
          ...${sessionOutputFragment.name}
        }
      }
      ${sessionOutputFragment.fragment}
    `,
  }),

  inventoryDeltas: () => ({
    query: gql`
      query {
        inventoryDeltas @client {
          ...${inventoryDeltasFragment.name}
        }
      }
      ${inventoryDeltasFragment.fragment}
    `,
    fetchPolicy: 'cache-only',
  }),

  setCurrentMove: input => ({
    mutation: gql`
      mutation setCurrentMove($input: SetMoveIQMoveInput) {
        setCurrentMove(input: $input) @client {
          CurrentMove {
            ...${currentMoveFragment.name}
          }
        }
      }
      ${currentMoveFragment.fragment}
    `,
    variables: {
      input,
    },
  }),

  setMoveIqMove: input => ({
    mutation: gql`
      mutation setMoveIqMove($input: SetMoveIqMoveInput) {
        setMoveIqMove(input: $input) @client {
          ...${moveIqMoveFragment.name}
        }
      }
      ${moveIqMoveFragment.fragment}
    `,
    variables: {
      input: transformsSetMoveIqMoveClientInput(input),
    },
  }),

  addInventoryDelta: input => ({
    mutation: gql`
      mutation addInventoryDelta($input: addInventoryDeltaInput) {
        addInventoryDelta(input: $input) @client
      }
    `,
    variables: {
      input,
    },
  }),

  clearInventoryDelta: () => ({
    mutation: gql`
      mutation {
        clearInventoryDelta @client
      }
    `,
  }),

  reservation: () => ({
    query: gql`
      query {
        CurrentMove @client {
          ...${currentMoveFragment.name}
        }
        Reservation @client {
          company {
            ...${companyFragment.name}
          }
          offer {
            id
            date
            price
            includePacking
            price
            valueScore
            valueTier
          }
          topValue
        }
      }
    ${currentMoveFragment.fragment}
    ${companyFragment.fragment}
  `,
  }),

  setReservation: input => ({
    mutation: gql`
      mutation setReservation($input: Reservation) {
        setReservation(input: $input) @client
      }
    `,
    variables: {
      input,
    },
  }),

  moveIqEstimates: () => ({
    query: gql`
      query {
        moveIqEstimates @client {
          ...${moveIqEstimateFragment.name}
        }
      }
    ${moveIqEstimateFragment.fragment}
  `,
  }),

  setMoveIqEstimate: input => ({
    mutation: gql`
      mutation setMoveIqEstimate($input: MoveIqEstimateRequest) {
        setMoveIqEstimate(input: $input) @client
      }
    `,
    variables: {
      input,
    },
  }),
};

function transformsSetMoveIqMoveRemoteInput(input) {
  const {
    moveId,
    fromAddressDetails,
    toAddressDetails,
    moveDate,
    homeSize,
  } = input;
  // TODO: Look into doing these transformations in apollo-link middleware
  return transformRemoteInput({
    moveId,
    fromAddressDetails: setDistanceToCurb(setStairCount(fromAddressDetails)),
    toAddressDetails: setDistanceToCurb(setStairCount(toAddressDetails)),
    moveDate,
    homeSize,
  });
}

function transformsSetMoveIqMoveClientInput({
  fromAddressDetails,
  toAddressDetails,
  ...input
}) {
  return {
    ...input,
    fromAddressDetails: remoteElevatorsToClient(fromAddressDetails),
    toAddressDetails: remoteElevatorsToClient(toAddressDetails),
  };
}

// All form values come back as string. Coerce them to the right type.
// TODO: We should be able to remove this once reflectedAttributes infers
// attribute type values.
function setStairCount(addressDetails) {
  switch (String(addressDetails.elevators)) {
    case 'true':
      return {
        ...addressDetails,
        elevators: true,
        stairCount: 0,
      };
    case 'false':
      return {
        ...addressDetails,
        elevators: false,
        stairCount: parseInt(addressDetails.stairCount, 10),
      };
    case 'null':
    default:
      return {
        ...addressDetails,
        elevators: false,
        stairCount: 0,
      };
  }
}

function setDistanceToCurb(addressDetails) {
  switch (String(addressDetails.distanceToCurb)) {
    case 'null':
    case '':
      return {
        ...addressDetails,
        distanceToCurb: 'UNSURE',
      };
    default:
      return addressDetails;
  }
}

function remoteElevatorsToClient(addressDetails) {
  if (addressDetails.elevators === false && addressDetails.stairCount === 0) {
    // if elevators is strictly false and stairs are strictly zero,
    // this is a remote MoveIqMove that has no stairs/no elevator (thus ground floor)
    // set the value to "null" so it can sync up with the client form
    return {
      ...addressDetails,
      elevators: 'null',
    };
  }
  return { ...addressDetails };
}

function makeEstimatesRequestInput(input) {
  return pick(input, [
    'companyIds',
    'date',
    'email',
    'firstName',
    'lastName',
    'moveId',
    'phone',
    'notes',
  ]);
}
